import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MapPin, X } from 'lucide-react';

import { closeModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';

import { Node } from '../context';

export interface ChooseDestinationPanoramaModalProps {
  currentNode: Node;
  nodes: Node[];
  onChoose: (node: Node) => void;
}

export const ChooseDestinationPanoramaModal: React.FC<ChooseDestinationPanoramaModalProps> = ({
  currentNode,
  nodes,
  onChoose,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Node | null>(null);

  const filteredNodes = nodes.filter((node) => node.id !== currentNode.id);

  const RenderNode = ({ node }: { node: Node }) => {
    const isAlreadyLinked = currentNode.links.some((link) => link.id === node.id);

    return (
      <button
        disabled={isAlreadyLinked}
        onClick={() => setSelected(node)}
        className="relative col-span-1 overflow-hidden rounded"
      >
        {selected?.id === node.id && (
          <div className="absolute inset-0 z-10 rounded border-2 border-primary-light"></div>
        )}
        {isAlreadyLinked && (
          <div className="absolute inset-0 z-10 rounded border-2 border-green-500"></div>
        )}
        <div
          className={`${selected?.id === node.id ? 'block' : 'hidden'} pointer-events-none absolute inset-0 z-10 rounded border-2 border-primary-light`}
        ></div>
        <img className="w-full cursor-pointer rounded opacity-75" src={node.panorama} />
        {node.focusPoints.length !== 0 && (
          <div className="absolute right-2 top-2 z-10 flex items-center justify-center gap-1 rounded bg-[#061B25]/70 px-2 py-1">
            <MapPin className="size-4" color="#fff" />
            <span className="text-xs text-white">{node.focusPoints.length}</span>
          </div>
        )}
        <div className="absolute bottom-0 w-full truncate bg-[#061B25]/70 px-2 py-2 text-center text-base text-white">
          {node.name}
        </div>
      </button>
    );
  };

  return (
    <ModalBase className="w-full max-w-screen-sm p-0" overlayWrapperClassname="z-[99999999]">
      <div className="flex justify-between rounded-t bg-[#061B25] px-5 py-4 text-white">
        <span className="text-base">Choose destination panorama</span>
        <button onClick={() => closeModal()}>
          <X />
        </button>
      </div>
      <div className="p-4">
        <div className="grid w-full grid-cols-2 gap-4">
          {filteredNodes.map((node) => (
            <RenderNode key={node.id} node={node} />
          ))}
        </div>
        <div className="flex items-center justify-center gap-4 pt-4">
          <Button
            disabled={!selected}
            onClick={() => {
              if (selected) onChoose(selected);
            }}
          >
            {t('Choose')}
          </Button>
          <Button variant="outline" onClick={closeModal}>
            {t('cancel')}
          </Button>
        </div>
      </div>
    </ModalBase>
  );
};
